import { useContext, useEffect } from "react";
import { Col, Form, Input, Row, Table, Typography } from "antd";
import { AssignmentContext } from "context/AssignmentContext/AssignmentContextProvider";
import { commaSeparateNumber } from "utils/functions";

const LineDetails = (props) => {
  // Contexts
  const { lineItemData } = useContext(AssignmentContext);

  // Antd
  const [lineDetailsForm] = Form.useForm();

  useEffect(() => {
    // Set form field values on first load
    if (lineItemData) {
      lineDetailsForm.setFieldsValue({
        style_no: lineItemData?.style?.style_no,
        internal_po: lineItemData?.po_map?.internal_po,
        original_po: lineItemData?.po_map?.original_po,
        line: lineItemData?.sewing_line?.name,
      });
    }
  }, [lineItemData, lineDetailsForm]);

  const columns = [
    {
      title: "Sub-Style",
      dataIndex: ["sub_style_info", "sub_style_no"],
      key: "sub_style",
      width: "15%",
    },
    {
      title: "Color",
      dataIndex: ["color_info", "name"],
      key: "color_name",
      width: "20%",
    },
    {
      title: "Size",
      dataIndex: ["size_info", "name"],
      key: "size_name",
    },
    {
      title: "Cut Qty",
      dataIndex: ["total_cutting"],
      key: "total_cutting",
      render(value, record) {
        return <span>{value ? commaSeparateNumber(value) : ""}</span>;
      },
    },
    {
      title: "This Line",
      dataIndex: ["assigned"],
      key: "assigned",
      render(value, record) {
        return <span>{value ? commaSeparateNumber(value) : ""}</span>;
      },
    },
    {
      title: "Total",
      dataIndex: ["total_assignment_all_line"],
      key: "total_assignment_all_line",
      render(value, record) {
        return <span>{value ? commaSeparateNumber(value) : ""}</span>;
      },
    },
    {
      title: "Assignment Remain",
      dataIndex: ["total_remain_assignment"],
      key: "total_remain_assignment",
      render(value, record) {
        return <span>{value ? commaSeparateNumber(value) : ""}</span>;
      },
    },
    {
      title: "Sewing Used",
      dataIndex: ["total_sewing_used"],
      key: "total_sewing_used",
      render(value, record) {
        return <span>{value ? commaSeparateNumber(value) : 0}</span>;
      },
    },
    {
      title: "Sewing Remaining",
      dataIndex: ["total_sewing_remain"],
      key: "total_sewing_remain",
      render(value, record) {
        return <span>{value ? commaSeparateNumber(value) : 0}</span>;
      },
    },
  ];

  const TableSummary = ({ pageData }) => {
    const cutQtySum = pageData?.reduce((total, item) => {
      return total + item.total_cutting;
    }, 0);
    const assignedThisLineQtySum = pageData?.reduce((total, item) => {
      return total + Number(item?.assigned);
    }, 0);
    const totalAssignAllLinesQtySum = pageData?.reduce((total, item) => {
      return total + item?.total_assignment_all_line;
    }, 0);
    const totalRemainAssignmentQtySum = pageData?.reduce((total, item) => {
      return total + item?.total_remain_assignment;
    }, 0);
    const totalSewingUsedQtySum = pageData?.reduce((total, item) => {
      return total + item?.total_sewing_used;
    }, 0);
    const totalSewingRemainQtySum = pageData?.reduce((total, item) => {
      return total + item?.total_sewing_remain;
    }, 0);

    return (
      <Table.Summary>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} id="total">
            Total
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} id="color" />
          <Table.Summary.Cell index={2} id="size" />
          <Table.Summary.Cell index={3} id="cut_qty">
            <Typography.Text>{cutQtySum}</Typography.Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4} id="assigned_this_line">
            <Typography.Text>{assignedThisLineQtySum}</Typography.Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} id="total_assigned_all_lines">
            <Typography.Text>{totalAssignAllLinesQtySum}</Typography.Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6} id="assignment_remain">
            <Typography.Text>{totalRemainAssignmentQtySum}</Typography.Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7} id="sewing_used">
            <Typography.Text>{totalSewingUsedQtySum}</Typography.Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={8} id="sewing_remain">
            <Typography.Text>{totalSewingRemainQtySum}</Typography.Text>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const getPrintableTableHtml = () => {
    const details = lineItemData?.line_assignment_details || [];
    const columns = [
      {
        title: "Sub-Style",
        key: "sub_style",
        get: (d) => d?.sub_style_info?.sub_style_no || "",
      },
      {
        title: "Color",
        key: "color_name",
        get: (d) => d?.color_info?.name || "",
      },
      { title: "Size", key: "size_name", get: (d) => d?.size_info?.name || "" },
      {
        title: "Cut Qty",
        key: "total_cutting",
        get: (d) => commaSeparateNumber(d?.total_cutting) || "",
      },
      {
        title: "This Line",
        key: "assigned",
        get: (d) => commaSeparateNumber(d?.assigned) || "",
      },
      {
        title: "Total",
        key: "total_assignment_all_line",
        get: (d) => commaSeparateNumber(d?.total_assignment_all_line) || "",
      },
      {
        title: "Assignment Remain",
        key: "total_remain_assignment",
        get: (d) => commaSeparateNumber(d?.total_remain_assignment) || "",
      },
      {
        title: "Sewing Used",
        key: "total_sewing_used",
        get: (d) => commaSeparateNumber(d?.total_sewing_used) || "0",
      },
      {
        title: "Sewing Remaining",
        key: "total_sewing_remain",
        get: (d) => commaSeparateNumber(d?.total_sewing_remain) || "0",
      },
    ];

    
    const summary = {
      cutQtySum: details.reduce((t, i) => t + (i.total_cutting || 0), 0),
      assignedThisLineQtySum: details.reduce(
        (t, i) => t + Number(i?.assigned || 0),
        0,
      ),
      totalAssignAllLinesQtySum: details.reduce(
        (t, i) => t + (i?.total_assignment_all_line || 0),
        0,
      ),
      totalRemainAssignmentQtySum: details.reduce(
        (t, i) => t + (i?.total_remain_assignment || 0),
        0,
      ),
      totalSewingUsedQtySum: details.reduce(
        (t, i) => t + (i?.total_sewing_used || 0),
        0,
      ),
      totalSewingRemainQtySum: details.reduce(
        (t, i) => t + (i?.total_sewing_remain || 0),
        0,
      ),
    };

    let html = `<table border="1" cellspacing="0" cellpadding="6" style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead>
        <tr>
          ${columns.map((col) => `<th>${col.title}</th>`).join("")}
        </tr>
      </thead>
      <tbody>
        ${details
          .map(
            (row) => `
          <tr>
            ${columns.map((col) => `<td>${col.get(row)}</td>`).join("")}
          </tr>
        `,
          )
          .join("")}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          <td></td>
          <td></td>
          <td>${summary.cutQtySum}</td>
          <td>${summary.assignedThisLineQtySum}</td>
          <td>${summary.totalAssignAllLinesQtySum}</td>
          <td>${summary.totalRemainAssignmentQtySum}</td>
          <td>${summary.totalSewingUsedQtySum}</td>
          <td>${summary.totalSewingRemainQtySum}</td>
        </tr>
      </tfoot>
    </table>`;
    return html;
  };

  // Print handler: use hidden iframe for printing
  const handlePrint = () => {
    const formValues = lineDetailsForm.getFieldsValue();
    const printableHtml = `
      <div class="form-row" style="margin-bottom:12px;">
        <span style="font-weight:bold;margin-right:8px;">Style:</span>
        <span style="margin-right:24px;">${formValues.style_no || ""}</span>
        <span style="font-weight:bold;margin-right:8px;">Internal PO:</span>
        <span style="margin-right:24px;">${formValues.internal_po || ""}</span>
        <span style="font-weight:bold;margin-right:8px;">Original PO:</span>
        <span style="margin-right:24px;">${formValues.original_po || ""}</span>
        <span style="font-weight:bold;margin-right:8px;">Line:</span>
        <span style="margin-right:24px;">${formValues.line || ""}</span>
      </div>
      ${getPrintableTableHtml()}
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #222; padding: 6px; text-align: left; }
        th { background: #f5f5f5; }
        tfoot td { font-weight: bold; background: #fafafa; }
      </style>
    `;
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(
      `<html><head><title>Print</title></head><body>${printableHtml}</body></html>`,
    );
    doc.close();
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 500);
  };

  return (
    <>
      <div
        style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}
      >
        <button
          type="button"
          onClick={handlePrint}
          style={{
            padding: "4px 16px",
            background: "#1677ff",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          Print
        </button>
      </div>
      <Form layout="vertical" form={lineDetailsForm}>
        <Row gutter={8}>
          <Col span={6}>
            <Form.Item name="style_no" label="Style">
              <Input disabled={true} size="small" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="internal_po" label="Internal PO">
              <Input disabled={true} size="small" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="original_po" label="Original PO">
              <Input disabled={true} size="small" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="line" label="Line">
              <Input disabled={true} size="small" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table
        pagination={false}
        size={"small"}
        rowClassName={() => "editable-row"}
        bordered
        dataSource={lineItemData?.line_assignment_details}
        columns={columns}
        sticky={true}
        summary={(pageData) => {
          return <TableSummary pageData={pageData} />;
        }}
      />
    </>
  );
};

export default LineDetails;
